/**
 * router/index.ts
 * Route table for the RIGX Operador PWA.
 *
 * v1 scope is "fallas de flota" (LOCX) — see
 * openspec/changes/archive/2026-07-20-add-locx-fleet-failure-reports (backend)
 * and project memory project_locx_operational_app_future_need. PR2b of
 * add-locx-trip-fuel-tracking adds "gerenciamiento de viaje" and "carga de
 * combustible" on top. EPP shipped as /epp (nomi-epp-firma).
 *   - /login              (public, fullscreen, no shell)
 *   - /                    (home, inside AppLayout — shell-nested)
 *   - /fallas              (mis fallas asignadas — list)
 *   - /fallas/nueva        (reportar una falla nueva)
 *   - /fallas/:id          (detail + FSM transitions)
 *   - /viajes              (mis viajes — list)
 *   - /viajes/nuevo        (solicitar un viaje nuevo)
 *   - /viajes/:id          (detail, read-only)
 *   - /combustible         (cargas de combustible — list)
 *   - /combustible/nueva   (registrar una carga nueva)
 *   - /combustible/:id     (detail + firma)
 *   - /documentos          (mis documentos + certificado laboral)
 *   - /epp                 (mis EPP: entregas, firma y constancia)
 *   - /calendario          (mi diagrama)
 *   - /ausencias           (Solicitudes: absences, services and purchases asked for)
 *   - /ausencias/nueva     (solicitar una ausencia nueva)
 *   - /organigrama         (organigrama, read-only)
 *   - /solicitudes-enviadas, /pedidos-repuestos — old Campo lists, now redirect to /ausencias
 *   - /escanear            (in-app QR scanner, full-screen camera)
 *   - /qr/:token           (a unit's documents and checklist, from a sticker)
 *
 * The 5 above (nomi-autoservicio-empleado Tier A) moved here from
 * apps/frontend 2026-09-14 — architecture correction: employee self-service
 * belongs in this app (app.rigx.sofistic.io), apps/frontend (rigx.sofistic.io) is
 * the admin/aprobación side only (see AttendanceEventsReportView.vue there,
 * unchanged). Same `/rrhh/self/*` backend routes, untouched.
 *
 * Guard: same posture as apps/frontend's scoped guard (FS-13), trimmed down.
 * Unauthenticated → /login; authenticated hitting /login → /. A hub route
 * (Campo/Yo) the user's capabilities leave empty → / (operator-tabs-
 * by-role). Nested screens stay ungated here: the backend guard answers 403
 * and the hubs never link to them for a user without the capability.
 *
 * operator-app-unificada T3 — every route inside AppLayout carries
 * `meta.title`, shown by the AppBar (the views no longer draw their own
 * title row or back link). `meta.hub` marks a tab root (Inicio, Campo,
 * Solicitudes, Mi legajo: no back chevron); `meta.tab` names the owning tab
 * when the path alone does not tell (ausencias/nueva → Solicitudes).
 */
import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { isAuthenticated } from '../lib/session.js';
import { currentCapabilities } from '../lib/capabilities.js';
import { hubFor, isHubVisible, type HubSection } from '../lib/service-registry.js';

declare module 'vue-router' {
  interface RouteMeta {
    /** Title shown by the AppBar (operator-app-unificada T3). */
    title?: string;
    /** A tab root of the bottom bar. */
    hub?: boolean;
    hubSection?: HubSection;
    /** Route name of the owning tab, when the path does not tell. */
    tab?: string;
    public?: boolean;
  }
}

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('../views/LoginView.vue'),
    meta: { public: true },
  },
  {
    // operator-qr-scanner — the in-app scanner. Full-screen camera, outside
    // AppLayout like /voz; a RigX sticker read here opens qr-unit below.
    path: '/escanear',
    name: 'qr-scan',
    component: () => import('../views/QrScanView.vue'),
  },
  {
    // locx-voz-multi-intencion (design.md D14) — app-shell mic entry point
    // (AppLayout.vue's topbar), reachable from anywhere. Outside AppLayout on
    // purpose, same posture as /qr/:token above: the recording stage (screen
    // 2) is a full-screen takeover, not a screen inside the normal chrome —
    // AppLayout's fixed topbar would sit on top of it otherwise. Requires
    // login (no `meta.public`), same guard as every other route below.
    // `assetId` query param (optional) forwards the scanned-unit asset into
    // the falla confirmation screen (task 9.3) — never a route param, since
    // most dictations (repuesto, or a falla with no scanned unit) have none.
    path: '/voz',
    name: 'voice-intake',
    component: () => import('../views/VoiceIntakeView.vue'),
  },
  {
    path: '/',
    component: () => import('../components/AppLayout.vue'),
    children: [
      {
        // Hoy — the worker's day at a glance (operator-home-hoy). `hub` meta
        // marks a tab of its own in the bottom TabBar.
        path: '',
        name: 'home',
        component: () => import('../views/HomeView.vue'),
        meta: { hub: true, title: 'Inicio' },
      },
      {
        path: 'campo',
        name: 'hub-campo',
        component: () => import('../views/HubView.vue'),
        props: { section: 'campo' },
        meta: { hub: true, hubSection: 'campo', title: hubFor('campo').title },
      },
      {
        path: 'yo',
        name: 'hub-yo',
        component: () => import('../views/HubView.vue'),
        props: { section: 'yo' },
        meta: { hub: true, hubSection: 'yo', title: hubFor('yo').title },
      },
      {
        // operator-scan-tab — the Equipo tab became the scanner; its only
        // entry (Organigrama) now lives in Yo. Old links land there.
        path: 'equipo',
        redirect: { name: 'hub-yo' },
      },
      {
        // fleet-qr-compliance PR4 — a unit's documents and checklist. Reached
        // from a printed sticker (the QR encodes this URL, scanned with the
        // phone's camera) or from the in-app scanner (/escanear). Requires
        // login (require-login-for-qr, 2026-08-20): a logged-out scan is
        // bounced to /login?redirect=/qr/:token. Inside AppLayout since
        // operator-qr-scanner, so the fixed tab bar is there too.
        path: 'qr/:token',
        name: 'qr-unit',
        component: () => import('../views/QrUnitView.vue'),
        meta: { title: 'Equipo', tab: 'hub-campo' },
      },
      {
        path: 'fallas',
        name: 'fallas-list',
        component: () => import('../views/FallasListView.vue'),
        meta: { title: 'Mis fallas' },
      },
      {
        // Registered BEFORE fallas/:id — "nueva" must never match as an :id
        // (same ordering requirement the backend uses for /rrhh/persons/me
        // vs. /rrhh/persons/:id).
        path: 'fallas/nueva',
        name: 'falla-nueva',
        component: () => import('../views/ReportarFallaView.vue'),
        meta: { title: 'Reportar falla' },
      },
      {
        path: 'fallas/:id',
        name: 'falla-detail',
        component: () => import('../views/FallaDetailView.vue'),
        meta: { title: 'Falla' },
      },
      {
        path: 'viajes',
        name: 'trips-list',
        component: () => import('../views/TripsListView.vue'),
        meta: { title: 'Mis viajes' },
      },
      {
        // Registered BEFORE viajes/:id — same "nuevo" vs. :id ordering
        // requirement as fallas/nueva above.
        path: 'viajes/nuevo',
        name: 'trip-new',
        component: () => import('../views/NuevoViajeView.vue'),
        meta: { title: 'Nuevo viaje' },
      },
      {
        path: 'viajes/:id',
        name: 'trip-detail',
        component: () => import('../views/TripDetailView.vue'),
        meta: { title: 'Viaje' },
      },
      {
        path: 'combustible',
        name: 'fuel-loads-list',
        component: () => import('../views/FuelLoadsListView.vue'),
        meta: { title: 'Cargas de combustible' },
      },
      {
        // Registered BEFORE combustible/:id — same ordering requirement.
        path: 'combustible/nueva',
        name: 'fuel-load-new',
        component: () => import('../views/NuevaCargaView.vue'),
        meta: { title: 'Nueva carga' },
      },
      {
        path: 'combustible/:id',
        name: 'fuel-load-detail',
        component: () => import('../views/FuelLoadDetailView.vue'),
        meta: { title: 'Carga de combustible' },
      },
      {
        path: 'documentos',
        name: 'mis-documentos',
        component: () => import('../views/MisDocumentosView.vue'),
        meta: { title: 'Mis documentos' },
      },
      {
        path: 'epp',
        name: 'mis-epp',
        component: () => import('../views/MisEppView.vue'),
        meta: { title: 'Mis EPP' },
      },
      // operator-app-unificada T6 — owned by Mi legajo through its tile.
      {
        path: 'capacitaciones',
        name: 'mis-capacitaciones',
        component: () => import('../views/MisCapacitacionesView.vue'),
        meta: { title: 'Mis capacitaciones' },
      },
      // odd/tasks/operator-panol.md — the pañolero on the phone (static 'recibir' before any param).
      {
        path: 'panol',
        name: 'panol',
        component: () => import('../views/PanolView.vue'),
        meta: { title: 'Pañol' },
      },
      {
        path: 'panol/recibir',
        name: 'panol-recibir',
        component: () => import('../views/PanolRecibirView.vue'),
        meta: { title: 'Recibir una OC' },
      },
      {
        path: 'calendario',
        name: 'mi-calendario',
        component: () => import('../views/MiCalendarioView.vue'),
        meta: { title: 'Mi diagrama' },
      },
      {
        path: 'ausencias',
        name: 'ausencias-list',
        component: () => import('../views/SolicitudesView.vue'),
        // operator-app-unificada T3 — the Solicitudes tab root.
        meta: { hub: true, title: 'Solicitudes' },
      },
      {
        // Registered BEFORE any ausencias/:id — same "nueva" vs. :id ordering
        // requirement as fallas/nueva above (no :id detail route exists yet
        // for an individual ausencia — the list itself is the full history).
        path: 'ausencias/nueva',
        name: 'ausencia-nueva',
        component: () => import('../views/NuevaAusenciaView.vue'),
        meta: { title: 'Licencia o vacaciones', tab: 'ausencias-list' },
      },
      {
        // operator-app-unificada T10 — the typed "Solicitar servicio" and
        // "Solicitud de compra" of the "¿Qué querés pedir?" sheet.
        path: 'solicitudes/servicio',
        name: 'servicio-nuevo',
        component: () => import('../views/NuevoServicioView.vue'),
        meta: { title: 'Solicitar servicio', tab: 'ausencias-list' },
      },
      {
        path: 'solicitudes/compra',
        name: 'compra-nueva',
        component: () => import('../views/NuevaCompraView.vue'),
        meta: { title: 'Solicitud de compra', tab: 'ausencias-list' },
      },
      {
        path: 'organigrama',
        name: 'organigrama',
        component: () => import('../views/OrganigramaView.vue'),
        meta: { title: 'Mi equipo' },
      },
      // Campo's old "Servicios Solicitados" and "Pedidos de repuestos" lists
      // live in the Solicitudes tab now (owner, 2026-11-04); old links land there.
      { path: 'solicitudes-enviadas', redirect: { name: 'ausencias-list' } },
      { path: 'pedidos-repuestos', redirect: { name: 'ausencias-list' } },
    ],
  },
  // Catch-all: unknown paths resolve to home (guard bounces to /login if unauthenticated).
  { path: '/:pathMatch(.*)*', redirect: { name: 'home' } },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach((to) => {
  const authed = isAuthenticated.value;

  if (!to.meta['public'] && !authed) {
    return { name: 'login', query: { redirect: to.fullPath } };
  }
  if (to.name === 'login' && authed) {
    return { name: 'home' };
  }
  // operator-tabs-by-role — a hub the user has no service in (e.g. Campo for
  // an HR-only user reached through an old link) falls back to Hoy.
  const hubSection = to.meta.hubSection;
  if (hubSection && !isHubVisible(hubSection, currentCapabilities.value)) {
    return { name: 'home' };
  }
  return true;
});
